/**
 * BFL (Black Forest Labs) Flux Pro 1.1 Ultra 文生图
 * 官方 API：POST /v1/flux-pro-1.1-ultra → polling_url → result.sample
 * 异步轮询封装为同步接口，返回 { urls, provider: 'official', sync: true }
 *
 * 文档：https://docs.bfl.ai/flux_models/flux_1_1_pro_ultra_raw
 */
import axios from 'axios'
import { extractApiErrorMessage } from '../../extract-api-error-message'

var BFL_PROXY_PATH = '/bfl'
var SUBMIT_PATH = '/v1/flux-pro-1.1-ultra'
var POLL_INTERVAL = 3000
var MAX_POLL_ATTEMPTS = 120 // 120 * 3s = 6 minutes max

var ASPECT_RATIOS = ['21:9', '16:9', '4:3', '3:2', '1:1', '2:3', '3:4', '9:16', '9:21']

function getBflConfig() {
  return {
    baseURL: BFL_PROXY_PATH,
    apiKey: process.env.VUE_APP_OFFICIAL_BFL_API_KEY || ''
  }
}

/**
 * 解析 BFL 返回的 polling_url，提取 host 和代理路径
 */
function parsePollingUrl(url) {
  if (!url) return null
  var match = url && url.match(/^https?:\/\/([^/]+\.bfl\.ai)(\/.*)/)
  if (match) {
    return { host: match[1], path: BFL_PROXY_PATH + match[2] }
  }
  return null
}

function getHeaders(apiKey) {
  return {
    'Content-Type': 'application/json',
    'x-key': apiKey
  }
}

function normalizeAspectRatio(v) {
  if (!v || typeof v !== 'string') return '1:1'
  var s = v.replace(/\uff1a/g, ':').trim()
  return ASPECT_RATIOS.indexOf(s) !== -1 ? s : '1:1'
}

/**
 * 递归轮询 BFL 结果接口，直到 status === 'Ready' 或出错/超时
 * @returns {Promise<string>} signed image URL
 */
function pollForResult(apiKey, pollingUrl, bflHost, attempts) {
  return axios.get(pollingUrl, {
    headers: { 'x-key': apiKey, 'X-Bfl-Target': bflHost },
    timeout: 30000
  }).then(function (res) {
    var data = res && res.data
    if (!data) {
      return Promise.reject(new Error('BFL API returned empty response'))
    }
    if (data.status === 'Ready') {
      var url = data.result && data.result.sample
      if (!url) {
        return Promise.reject(new Error('BFL API returned Ready but no sample URL'))
      }
      return url
    }
    if (data.status === 'Request Moderated') {
      return Promise.reject(new Error('Request was moderated by content safety system. Please adjust your prompt and try again.'))
    }
    if (data.status === 'Content Moderated') {
      return Promise.reject(new Error('Generated content was flagged by content moderation. Please modify your prompt and try again.'))
    }
    if (data.status === 'Error') {
      var errMsg = ''
      if (typeof data.error === 'string') {
        errMsg = data.error
      } else if (data.error && typeof data.error === 'object') {
        errMsg = data.error.message || data.error.msg || JSON.stringify(data.error)
      }
      return Promise.reject(new Error(errMsg || 'BFL generation failed'))
    }
    if (attempts <= 0) {
      return Promise.reject(new Error('BFL generation timed out'))
    }
    return new Promise(function (resolve) {
      setTimeout(function () {
        resolve(pollForResult(apiKey, pollingUrl, bflHost, attempts - 1))
      }, POLL_INTERVAL)
    })
  })
}

/**
 * 下载 BFL 签名图片并上传到自有服务器，返回服务器 URL
 * 通过 /bfl 代理下载以避免 Azure Blob Storage 的 CORS 限制
 * @param {string} signedUrl - BFL 返回的临时签名 URL
 * @param {string} filename - 上传文件名
 * @returns {Promise<string>} 服务器图片 URL
 */
function downloadToBlob(signedUrl, filename) {
  var parsed = signedUrl.match(/^https?:\/\/([^/]+)(\/.*)/)
  if (!parsed) {
    return Promise.reject(new Error('Invalid BFL image URL'))
  }
  var host = parsed[1]
  var path = BFL_PROXY_PATH + parsed[2]
  return axios.get(path, {
    responseType: 'arraybuffer',
    timeout: 60000,
    headers: { 'X-Bfl-Target': host }
  }).then(function (res) {
    var blob = new Blob([res.data], { type: 'image/png' })
    console.log('[BFL Ultra] image blob downloaded', blob.size, 'bytes')
    return blob
  })
}

/**
 * 提交 BFL Flux Pro 1.1 Ultra 生成请求并轮询结果
 * @param {Object} params - prompt(必填), aspectRatio, count
 * @returns {Promise<{ urls: string[], provider: 'official', sync: true }>}
 */
export function submitBflFluxPro11UltraImage(params) {
  var cfg = getBflConfig()
  if (!cfg.baseURL) {
    return Promise.reject(new Error('未配置 VUE_APP_OFFICIAL_BFL_BASE_URL'))
  }
  if (!cfg.apiKey || cfg.apiKey === 'your-bfl-api-key-here') {
    return Promise.reject(new Error('未配置 VUE_APP_OFFICIAL_BFL_API_KEY'))
  }

  var prompt = (params.prompt || '').trim()
  if (!prompt) {
    return Promise.reject(new Error('prompt 必填'))
  }

  var count = Math.min(4, Math.max(1, parseInt(params.count, 10) || 1))

  var body = {
    prompt: prompt.slice(0, 1000),
    aspect_ratio: normalizeAspectRatio(params.aspectRatio || params.aspect_ratio),
    output_format: 'png'
  }

  // 提交单张请求
  function submitOne() {
    return axios.post(cfg.baseURL + SUBMIT_PATH, body, {
      headers: getHeaders(cfg.apiKey),
      timeout: 30000
    }).then(function (res) {
      var data = res && res.data
      var rawPollingUrl = data && (data.polling_url || (data.id ? cfg.baseURL + '/v1/get_result?id=' + data.id : null))
      var parsed = parsePollingUrl(rawPollingUrl)
      if (!parsed) {
        return Promise.reject(new Error('BFL API 未返回 polling_url'))
      }
      return pollForResult(cfg.apiKey, parsed.path, parsed.host, MAX_POLL_ATTEMPTS)
        .then(function (signedUrl) {
          return downloadToBlob(signedUrl, 'flux-pro-1-1-ultra-' + Date.now() + '.png')
        })
    })
  }

  // 并行提交多张
  var promises = []
  for (var i = 0; i < count; i++) {
    promises.push(submitOne())
  }

  return Promise.all(promises).then(function (blobs) {
    return { urls: blobs.map(function() { return '' }), blobs: blobs, provider: 'official', sync: true }
  }).catch(function (err) {
    var msg = extractApiErrorMessage(err.response && err.response.data) || err.message || '请求失败'
    return Promise.reject(new Error(msg))
  })
}
